import { parseApiErrorText } from '@/lib/api/error-utils'
import { RefreshAttemptBudget } from '@/lib/api/refresh-attempt-budget'
import { createLogger, setLoggerRequestId } from '@/lib/logger'
import {
  clearWebViewCookies,
  desktopSecureStorage,
  getPlatform,
  isTauri,
} from '@/lib/utils/tauri'
import { useAuth as useAuthStore } from '@/store/auth.store'
import { useOrganizationStore } from '@/store/organization.store'
import { env } from '@/utils/env'
import { toast } from 'sonner'

import { ApiRequestConfig } from '@scrambl/shared'
import { linkAbortSignals } from '@scrambl/shared/session-scope'

import { fetchWithDeadline } from './fetch-with-deadline'
import { captureIdentityScope, captureRequestScope } from './request-scope'

const logger = createLogger('lib/api/api-client')

const apiTrafficLogState = {
  samplesByEndpoint: new Map<
    string,
    {
      count: number
      dedupeCount: number
      windowStart: number
    }
  >(),
  summaryWindowMs: 5000,
  minRequestsForSummary: 3,
}

const normalizeTrackedEndpoint = (endpoint: string): string => {
  const [rawPath, rawQuery] = endpoint.split('?')
  const path = rawPath
    .replace(/\/[0-9a-f]{8}-[0-9a-f-]{27,}/gi, '/:id')
    .replace(/\/\d+/g, '/:id')

  if (!rawQuery) {
    return path
  }

  const params = new URLSearchParams(rawQuery)
  const trackedKeys = [
    'page',
    'limit',
    'sortBy',
    'sortOrder',
    'assigneeIds',
    'assignees',
    'search',
    'moduleType',
    'moduleId',
    'excludeCompleted',
    'includePersonalTasks',
    'includeSubtasks',
    'view',
    'status',
  ]

  const normalizedPairs = trackedKeys
    .map(key => {
      const values = params.getAll(key)
      if (!values.length) {
        return []
      }
      return values.sort().map(value => `${key}=${value}`)
    })
    .flat()

  return normalizedPairs.length ? `${path}?${normalizedPairs.join('&')}` : path
}

const isTrackedEndpoint = (endpoint: string): boolean => {
  return endpoint.startsWith('/tasks') || endpoint.startsWith('/my-tasks')
}

const trackApiTraffic = (
  endpoint: string,
  method: string,
  dedupeHit = false,
): void => {
  if (!import.meta.env.DEV || !isTrackedEndpoint(endpoint)) {
    return
  }

  const now = Date.now()
  const signature = `${method.toUpperCase()} ${normalizeTrackedEndpoint(endpoint)}`
  const sample = apiTrafficLogState.samplesByEndpoint.get(signature) || {
    count: 0,
    dedupeCount: 0,
    windowStart: now,
  }

  sample.count += 1
  if (dedupeHit) {
    sample.dedupeCount += 1
  }

  const elapsedMs = now - sample.windowStart
  if (
    elapsedMs >= apiTrafficLogState.summaryWindowMs &&
    sample.count >= apiTrafficLogState.minRequestsForSummary
  ) {
    logger.warn(
      `[ApiClient][Perf] ${signature} in ${Math.round(elapsedMs)}ms`,
      {
        requests: sample.count,
        dedupeHits: sample.dedupeCount,
        inFlight: Object.keys(pendingRequests).length,
      },
    )

    sample.count = 0
    sample.dedupeCount = 0
    sample.windowStart = now
  }

  apiTrafficLogState.samplesByEndpoint.set(signature, sample)
}

/**
 * apiClient represents an empty 200 body as a truthy `{ success: true }`
 * sentinel (see the isEmpty branch below). A NestJS handler that returns null
 * sends exactly that empty body, so any endpoint whose answer can be "there is
 * none" comes back truthy and defeats `if (result)` checks at the call site.
 *
 * API methods declaring a nullable return type must pass their result through
 * this so callers can test it for truthiness. Only the exact one-key sentinel
 * is converted, so a real payload is never swallowed.
 */
export function emptyResponseToNull<T>(value: T | null | undefined): T | null {
  if (value == null) return null

  if (
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.keys(value).length === 1 &&
    (value as { success?: unknown }).success === true
  ) {
    return null
  }

  return value
}

export class ApiError extends Error {
  status: number
  data: unknown

  constructor(message: string, status: number, data?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.data = data
  }
}

// Request deduplication cache
interface PendingRequest<T> {
  promise: Promise<T>
  timestamp: number
}

// Global in-flight request cache
const pendingRequests: Record<string, PendingRequest<unknown>> = {}

// Generate a unique request key based on method, url, body, and organization
function getRequestKey(
  method: string,
  url: string,
  body?: unknown,
  organizationId?: string,
  externalRelationshipId?: string,
): string {
  const bodyKey = body ? JSON.stringify(body) : ''
  const orgKey = organizationId ? `org:${organizationId}` : ''
  const relationshipKey = externalRelationshipId
    ? `external:${externalRelationshipId}`
    : ''
  return `${method}:${url}:${bodyKey}:${orgKey}:${relationshipKey}:user:${useAuthStore.getState().user?.id ?? ''}:generation:${captureRequestScope().generation}`
}

// Cache expiration time for in-flight requests (30 seconds)
const REQUEST_CACHE_EXPIRATION = 30000

const GET_START_WINDOW_MS = 1000
const MAX_GET_STARTS_PER_WINDOW = 8
const GET_RETRY_BASE_DELAY_MS = 750
const GET_RETRY_MAX_ATTEMPTS = 2
const getRequestStartTimes: number[] = []
let getRequestSlotQueue = Promise.resolve()

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

function pruneGetRequestStartTimes(now: number): void {
  while (
    getRequestStartTimes.length > 0 &&
    now - getRequestStartTimes[0] >= GET_START_WINDOW_MS
  ) {
    getRequestStartTimes.shift()
  }
}

async function waitForGetRequestSlot(
  method: string,
  endpoint: string,
): Promise<void> {
  if (
    method !== 'GET' ||
    endpoint.startsWith('/auth/') ||
    endpoint.startsWith('/client-portal/') ||
    endpoint.startsWith('/portal/magic/')
  ) {
    return
  }

  const waitForSlot = async () => {
    let hasSlot = false
    while (!hasSlot) {
      const now = Date.now()
      pruneGetRequestStartTimes(now)

      if (getRequestStartTimes.length < MAX_GET_STARTS_PER_WINDOW) {
        getRequestStartTimes.push(now)
        hasSlot = true
        continue
      }

      const oldestStart = getRequestStartTimes[0]
      const waitMs = Math.max(0, GET_START_WINDOW_MS - (now - oldestStart)) + 25
      await delay(waitMs)
    }
  }

  const slotPromise = getRequestSlotQueue.then(waitForSlot, waitForSlot)
  getRequestSlotQueue = slotPromise.catch(() => undefined)
  await slotPromise
}

function getRetryAfterDelayMs(response: Response, attempt: number): number {
  const retryAfter = response.headers.get('retry-after')
  if (retryAfter) {
    const retryAfterSeconds = Number(retryAfter)
    if (Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0) {
      return retryAfterSeconds * 1000
    }

    const retryAfterDate = Date.parse(retryAfter)
    if (!Number.isNaN(retryAfterDate)) {
      return Math.max(0, retryAfterDate - Date.now())
    }
  }

  return GET_RETRY_BASE_DELAY_MS * Math.pow(2, attempt)
}

// Bound refresh attempts so a provider outage or a misclassified rejection
// cannot fan out into an unending request loop.
const MAX_REFRESH_ATTEMPTS = 2
const REFRESH_ATTEMPT_WINDOW = 30000 // 30 seconds
const refreshAttemptBudget = new RefreshAttemptBudget(
  MAX_REFRESH_ATTEMPTS,
  REFRESH_ATTEMPT_WINDOW,
)
let isRefreshInProgress = false
let pendingRefreshPromise: Promise<boolean> | null = null
let hasRedirectedToLogin = false // Prevent multiple redirects

export type RefreshFailureReason =
  | 'none'
  | 'missing_refresh_token'
  | 'unauthorized'
  | 'transient'
let lastRefreshFailureReason: RefreshFailureReason = 'none'

export function getLastRefreshFailureReason(): RefreshFailureReason {
  return lastRefreshFailureReason
}

function isTransientRefreshFailure(reason: RefreshFailureReason): boolean {
  return reason === 'transient'
}

function classifyRefreshError(error: unknown): RefreshFailureReason {
  if (error instanceof ApiError) {
    if (error.status === 401 || error.status === 403) {
      return 'unauthorized'
    }
    if (error.status >= 500) {
      return 'transient'
    }
  }

  if (error instanceof Error) {
    const message = error.message.toLowerCase()
    if (
      message.includes('failed to fetch') ||
      message.includes('network') ||
      message.includes('timeout')
    ) {
      return 'transient'
    }
  }

  return 'transient'
}

// Track initial auth check to prevent requests during app initialization
let isInitialAuthCheckComplete = false
let initialAuthCheckPromise: Promise<void> | null = null

// Called by AuthProvider when initial auth check completes (success or failure)
export function markInitialAuthCheckComplete(): void {
  logger.info('[ApiClient] Initial auth check marked as complete')
  isInitialAuthCheckComplete = true
  initialAuthCheckPromise = null
}

// Wait for initial auth check to complete
async function waitForInitialAuthCheck(): Promise<void> {
  // If initial auth check is complete AND no refresh is in progress, we're good
  if (isInitialAuthCheckComplete && !isRefreshInProgress) {
    return
  }

  // Wait for any in-progress refresh first
  if (isRefreshInProgress && pendingRefreshPromise) {
    debugLog(
      '[ApiClient] Waiting for in-progress refresh before initial auth check',
    )
    await pendingRefreshPromise
  }

  if (isInitialAuthCheckComplete) {
    return
  }

  // Create a promise that resolves when initial auth is complete
  if (!initialAuthCheckPromise) {
    initialAuthCheckPromise = new Promise<void>(resolve => {
      const checkInterval = setInterval(() => {
        // Check for both initial auth complete AND no refresh in progress
        if (isInitialAuthCheckComplete && !isRefreshInProgress) {
          clearInterval(checkInterval)
          resolve()
        }
      }, 50)

      // Timeout after 10 seconds to prevent infinite waiting
      setTimeout(() => {
        clearInterval(checkInterval)
        debugWarn('[ApiClient] Timeout waiting for initial auth check')
        resolve()
      }, 10000)
    })
  }

  await initialAuthCheckPromise
}

// Public paths where we should NOT redirect to login on auth failure
// This prevents infinite redirect loops when on login/register pages
const PUBLIC_AUTH_PATHS = [
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/auth/',
  '/organization-setup',
  '/user-setup',
  '/invite',
  '/client-portal/invite',
  '/portal/',
  '/f/', // Public forms
  '/embed/', // Embedded forms
  '/csat/', // Public CSAT survey
  '/unsubscribe/', // Public email unsubscribe landing
]

// Check if current page is a public auth page where we shouldn't redirect
function isOnPublicAuthPage(): boolean {
  const pathname = window.location.pathname
  return PUBLIC_AUTH_PATHS.some(path => pathname.startsWith(path))
}

// Check if current page is a client portal page (these don't use org context)
export function isOnClientPortalPage(): boolean {
  if (typeof window === 'undefined') return false
  const pathname = window.location.pathname
  return (
    pathname.startsWith('/client-portal') || pathname.startsWith('/portal/')
  )
}

const AUTH_READY_TIMEOUT_MS = 5000
const ORG_READY_TIMEOUT_MS = 5000
const ORG_READY_POLL_MS = 50

async function waitForAuthReady(): Promise<void> {
  const authState = useAuthStore.getState()
  if (authState.isAuthReady) {
    // Auth is ready, but also wait for any in-progress refresh
    // This prevents requests from going out during token refresh
    if (isRefreshInProgress && pendingRefreshPromise) {
      debugLog('[ApiClient] Auth ready but refresh in progress, waiting...')
      await pendingRefreshPromise
    }
    return
  }

  try {
    await authState.waitForAuthReady()
    // After auth is ready, also wait for any refresh that might have been triggered
    if (isRefreshInProgress && pendingRefreshPromise) {
      debugLog(
        '[ApiClient] Auth became ready but refresh in progress, waiting...',
      )
      await pendingRefreshPromise
    }
  } catch (error) {
    debugWarn('[ApiClient] Timeout waiting for auth to be ready', error)
  }
}

async function waitForOrganizationContext(): Promise<void> {
  const start = Date.now()
  while (Date.now() - start < ORG_READY_TIMEOUT_MS) {
    const { currentOrganization } = useOrganizationStore.getState()
    if (currentOrganization?.id) {
      return
    }
    await new Promise(resolve => setTimeout(resolve, ORG_READY_POLL_MS))
  }
  debugWarn('[ApiClient] Timeout waiting for organization context')
}

// Cross-tab refresh coordination (web only — tabs share the auth cookies).
// If another tab just refreshed, the new access token cookie is already in
// place, so re-refreshing is wasted work and would race token rotation if
// that is ever enabled on the Cognito app client.
const CROSS_TAB_REFRESH_KEY = 'scrambl:lastTokenRefreshAt'
const CROSS_TAB_REFRESH_WINDOW = 15000 // 15 seconds

function wasTokenRefreshedRecently(): boolean {
  try {
    const raw = localStorage.getItem(CROSS_TAB_REFRESH_KEY)
    if (!raw) return false
    const timestamp = Number(raw)
    return (
      Number.isFinite(timestamp) &&
      Date.now() - timestamp < CROSS_TAB_REFRESH_WINDOW
    )
  } catch {
    return false
  }
}

function markTokenRefreshTimestamp(): void {
  try {
    localStorage.setItem(CROSS_TAB_REFRESH_KEY, String(Date.now()))
  } catch {
    // localStorage unavailable — cross-tab coordination degrades gracefully
  }
}

type LockManagerLike = {
  request: (name: string, callback: () => Promise<boolean>) => Promise<boolean>
}

export async function refreshAccessToken(): Promise<boolean> {
  if (isRefreshInProgress && pendingRefreshPromise) {
    logger.debug('[ApiClient] Token refresh already in progress, waiting...')
    return pendingRefreshPromise
  }

  if (!refreshAttemptBudget.canAttempt()) {
    logger.warn(
      '[ApiClient] Refresh circuit open; suppressing another attempt',
      {
        attempts: refreshAttemptBudget.count,
        reason: lastRefreshFailureReason,
      },
    )
    return false
  }

  logger.info('[ApiClient] Starting token refresh')
  isRefreshInProgress = true
  refreshAttemptBudget.recordAttempt()
  lastRefreshFailureReason = 'none'

  const refreshScope = captureIdentityScope()
  const performRefresh = async (): Promise<boolean> => {
    const desktopAccountId = isTauri()
      ? await desktopSecureStorage.getActiveAccountId()
      : null
    // Build headers for refresh request
    const refreshHeaders: Record<string, string> = {}
    if (isTauri()) {
      // For desktop, include refresh token in Authorization header if we have it
      const tokens = await desktopSecureStorage.getTokens()
      if (tokens?.refreshToken) {
        refreshHeaders['Authorization'] = `Bearer ${tokens.refreshToken}`
        logger.debug('[ApiClient] Using refresh token for refresh request')
      } else {
        logger.warn(
          '[ApiClient] No refresh token in storage for refresh request',
        )
        lastRefreshFailureReason = 'missing_refresh_token'
        return false
      }
      refreshHeaders['x-platform'] = 'desktop'
    }

    refreshScope.assertCurrent()
    const refreshResponse = await apiClient<{
      success: boolean
      tokens?: {
        accessToken: string
        refreshToken: string
        idToken?: string
      }
    }>('/auth/refresh', {
      method: 'POST',
      withAuth: true,
      skipDeduplication: true,
      headers: refreshHeaders,
    })

    if (!refreshResponse.success || !refreshResponse.tokens?.accessToken) {
      const message = String(
        (refreshResponse as { message?: string }).message ?? '',
      ).toLowerCase()
      if (message.includes('no refresh token')) {
        lastRefreshFailureReason = 'missing_refresh_token'
      } else if (
        message.includes('expired') ||
        message.includes('invalid') ||
        message.includes('unauthorized')
      ) {
        lastRefreshFailureReason = 'unauthorized'
      } else {
        lastRefreshFailureReason = 'transient'
      }
      return false
    }

    refreshScope.assertCurrent()
    // For desktop, save the new tokens to secure storage
    if (isTauri()) {
      await desktopSecureStorage.saveTokens(
        {
          accessToken: refreshResponse.tokens.accessToken,
          refreshToken: refreshResponse.tokens.refreshToken,
          idToken: refreshResponse.tokens.idToken,
        },
        { expectedActiveAccountId: desktopAccountId ?? undefined },
      )
      logger.info('[ApiClient] Desktop tokens saved after refresh')
    }

    markTokenRefreshTimestamp()
    logger.info('[ApiClient] Token refresh completed: success')
    return true
  }

  pendingRefreshPromise = (async () => {
    try {
      if (!isTauri()) {
        if (wasTokenRefreshedRecently()) {
          logger.debug(
            '[ApiClient] Skipping refresh — another tab refreshed recently',
          )
          return true
        }

        // Serialize refreshes across tabs when the Web Locks API is available
        const locks = (navigator as Navigator & { locks?: LockManagerLike })
          .locks
        if (locks?.request) {
          return await locks.request('scrambl-token-refresh', async () => {
            if (wasTokenRefreshedRecently()) {
              logger.debug(
                '[ApiClient] Skipping refresh — another tab refreshed while waiting for lock',
              )
              return true
            }
            return performRefresh()
          })
        }
      }

      return await performRefresh()
    } catch (error) {
      lastRefreshFailureReason = classifyRefreshError(error)
      logger.error('[ApiClient] Token refresh error:', error)
      return false
    } finally {
      isRefreshInProgress = false
      pendingRefreshPromise = null
    }
  })()

  const refreshSuccess = await pendingRefreshPromise
  if (refreshSuccess) {
    refreshAttemptBudget.reset()
    hasRedirectedToLogin = false
    lastRefreshFailureReason = 'none'
  }

  return refreshSuccess
}

// Clean expired pending requests
function cleanExpiredRequests() {
  const now = Date.now()
  Object.keys(pendingRequests).forEach(key => {
    const request = pendingRequests[key]
    if (request && now - request.timestamp > REQUEST_CACHE_EXPIRATION) {
      delete pendingRequests[key]
    }
  })
}

// Get the API URL to use for requests
function getApiUrl(endpoint: string): string {
  // Use build-time environment variable
  const apiUrl = env.apiUrl

  // For all other endpoints, use the default API URL (port 4005)
  return apiUrl
}

// Add a debug flag that can be controlled via localStorage or environment variables
const DEBUG_MODE = false // Disabled debug logging

// Utility for conditional logging
function debugLog(..._args: unknown[]) {
  // Debug logging intentionally disabled - enable DEBUG_MODE to use
}

function debugWarn(...args: unknown[]) {
  if (DEBUG_MODE) {
    logger.warn(...args)
  }
}

function debugError(...args: unknown[]) {
  // Always log errors, but prefix differently in production
  logger.error(...args)
}

function isRequestAbortError(error: unknown): boolean {
  if (error instanceof DOMException && error.name === 'AbortError') {
    return true
  }

  if (error instanceof Error) {
    const message = error.message.toLowerCase()
    return error.name === 'AbortError' || message.includes('signal is aborted')
  }

  return false
}

// Check if the endpoint needs organization ID in the header
function needsOrganizationId(endpoint: string): boolean {
  // Skip organization context for auth endpoints
  if (endpoint.startsWith('/auth/')) {
    return false
  }

  // Portal endpoints are scoped by portal session cookies and intentionally do not use the
  // normal app organization context. Waiting for org context here adds visible
  // latency for portal users.
  if (
    endpoint.startsWith('/client-portal/') ||
    endpoint.startsWith('/portal/magic/')
  ) {
    return false
  }

  // Skip for public endpoints
  const publicEndpoints = [
    '/public',
    '/health',
    '/status', // This should not match /status-templates
  ]

  // Check if the endpoint is a public endpoint (exact match or with trailing slash)
  if (
    publicEndpoints.some(
      prefix => endpoint === prefix || endpoint.startsWith(prefix + '/'),
    )
  ) {
    return false
  }

  // Skip organization context for user profile images (they're user-specific, not org-specific)
  if (endpoint.includes('/profile-image')) {
    return false
  }

  // Skip organization context for organizations endpoints when fetching org list
  if (
    endpoint === '/organizations' ||
    endpoint === '/organizations/with-logos'
  ) {
    return false
  }

  // Allow direct organization lookup by id without requiring current org context.
  // This is needed on /organizations when accepting an invite and switching orgs.
  if (/^\/organizations\/[0-9a-f-]+$/i.test(endpoint)) {
    return false
  }

  // Skip organization context for org-invite endpoints that are user/token scoped.
  // These must work on /organizations before any org is selected.
  if (
    endpoint === '/organization-invites' ||
    endpoint.startsWith('/organization-invites?') ||
    endpoint.startsWith('/organization-invites/email/') ||
    endpoint.startsWith('/organization-invites/token/') ||
    endpoint === '/organization-invites/accept' ||
    endpoint === '/organization-invites/decline'
  ) {
    return false
  }

  // Skip organization context for user's external org access (not org-specific)
  if (endpoint === '/external-orgs/my-host-orgs') {
    return false
  }

  // Explicitly include client endpoints
  if (endpoint.startsWith('/entities')) {
    return true
  }

  // By default, include organization context for all other endpoints
  return true
}

// Should deduplicate request?
function shouldDeduplicate(endpoint: string, method: string): boolean {
  // Don't deduplicate non-GET requests unless specifically allowed
  if (method !== 'GET') return false

  // Don't deduplicate auth endpoints
  if (endpoint.startsWith('/auth/')) return false

  // Specific endpoints to avoid deduplication
  const noDedupEndpoints = ['/organizations/invite', '/upload', '/media']

  return !noDedupEndpoints.some(prefix => endpoint.includes(prefix))
}

// Configure default fetch options based on platform
function getDefaultFetchOptions(): RequestInit {
  const platform = getPlatform()
  return {
    headers: {
      'Content-Type': 'application/json',
      'x-platform': platform, // Identify platform (web or desktop)
    },
    credentials: 'include', // Always include credentials to send cookies
    mode: 'cors', // Explicitly set CORS mode
  }
}

function parseJwtExp(accessToken: string): number | null {
  try {
    const parts = accessToken.split('.')
    if (parts.length < 2 || !parts[1]) return null
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const padding = base64.length % 4
    const padded = padding ? `${base64}${'='.repeat(4 - padding)}` : base64
    const payload = JSON.parse(atob(padded)) as { exp?: number }
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null
  } catch {
    return null
  }
}

function shouldPreemptivelyRefreshDesktopToken(accessToken: string): boolean {
  // Refresh if token expires in <= 2 minutes to avoid initial 401 bursts
  const expMs = parseJwtExp(accessToken)
  if (!expMs) return false
  return expMs - Date.now() <= 2 * 60 * 1000
}

// Get authorization header for desktop platform
async function getDesktopAuthHeader(
  endpoint: string,
  withAuth: boolean,
): Promise<Record<string, string>> {
  if (!isTauri()) return {}

  try {
    let tokens = await desktopSecureStorage.getTokens()
    if (tokens?.accessToken) {
      const canRefreshHere = withAuth && !endpoint.includes('/auth/refresh')

      if (canRefreshHere) {
        if (isRefreshInProgress && pendingRefreshPromise) {
          await pendingRefreshPromise
          tokens = await desktopSecureStorage.getTokens()
        } else if (shouldPreemptivelyRefreshDesktopToken(tokens.accessToken)) {
          logger.info(
            `[ApiClient] Desktop token near expiry, refreshing before request: ${endpoint}`,
          )
          const refreshed = await refreshAccessToken()
          if (refreshed) {
            tokens = await desktopSecureStorage.getTokens()
          }
        }
      }

      if (!tokens?.accessToken) {
        return {}
      }

      // Log last 8 chars of token to help debug token usage without exposing full token
      logger.debug(
        `[ApiClient] Using desktop access token: ...${tokens.accessToken.slice(-8)}`,
      )
      return { Authorization: `Bearer ${tokens.accessToken}` }
    } else {
      logger.debug('[ApiClient] No access token in desktop storage')
    }
  } catch (error) {
    logger.error('[ApiClient] Failed to get desktop auth token:', error)
  }

  return {}
}

// Check if endpoint needs aggressive cache busting.
// The API already sends Cache-Control: no-store on dynamic endpoints and React
// Query owns data freshness, so cache-busting `_t` params are only needed where
// an intermediary might otherwise serve a stale auth response.
function needsTimestamp(endpoint: string): boolean {
  const timestampEndpoints = [
    '/auth/me', // User auth status can change
  ]

  return timestampEndpoints.some(prefix => endpoint.includes(prefix))
}

// Cache organization context to avoid repeated store imports
let cachedOrganizationContext: {
  orgId: string | null
  slug: string | null
  externalRelationshipId: string | null
  timestamp: number
} | null = null
const ORG_CONTEXT_CACHE_TTL = 5000 // 5 seconds

// Clear cached context for debugging
if (import.meta.env.DEV) {
  cachedOrganizationContext = null
}

// Get current organization context with caching
async function getOrganizationContext(): Promise<{
  orgId: string | null
  slug: string | null
  externalRelationshipId: string | null
}> {
  const now = Date.now()

  try {
    // Use organization store to get current organization context
    const { currentOrganization, externalOrgRelationship } =
      useOrganizationStore.getState()

    const freshContext = {
      orgId: currentOrganization?.id || null,
      slug: currentOrganization?.slug || null,
      externalRelationshipId:
        currentOrganization?.isExternalAccess === true
          ? externalOrgRelationship?.id || null
          : null,
    }

    // Check if cached context matches current organization state
    if (
      cachedOrganizationContext &&
      now - cachedOrganizationContext.timestamp < ORG_CONTEXT_CACHE_TTL
    ) {
      const cachedMatches =
        cachedOrganizationContext.orgId === freshContext.orgId &&
        cachedOrganizationContext.slug === freshContext.slug &&
        cachedOrganizationContext.externalRelationshipId ===
          freshContext.externalRelationshipId

      if (cachedMatches) {
        debugLog(
          '[ApiClient] Using cached organization context (matches current):',
          cachedOrganizationContext,
        )
        return {
          orgId: cachedOrganizationContext.orgId,
          slug: cachedOrganizationContext.slug,
          externalRelationshipId:
            cachedOrganizationContext.externalRelationshipId,
        }
      } else {
        debugLog('[ApiClient] Cached context is stale, invalidating:', {
          cached: {
            orgId: cachedOrganizationContext.orgId,
            slug: cachedOrganizationContext.slug,
          },
          current: freshContext,
        })
        cachedOrganizationContext = null // Invalidate stale cache
      }
    }

    debugLog('[ApiClient] Fresh organization context from store:', {
      currentOrganization,
      hasId: !!currentOrganization?.id,
      hasSlug: !!currentOrganization?.slug,
      freshContext,
    })

    // Cache the fresh context
    cachedOrganizationContext = {
      ...freshContext,
      timestamp: now,
    }

    debugLog(
      '[ApiClient] Cached new organization context:',
      cachedOrganizationContext,
    )

    return freshContext
  } catch (error) {
    debugWarn('[ApiClient] Could not get organization context:', error)
    return { orgId: null, slug: null, externalRelationshipId: null }
  }
}

// Main API client function
export async function apiClient<TResponse, TRequest = unknown>(
  endpoint: string,
  config: ApiRequestConfig<TRequest> = {},
): Promise<TResponse> {
  const initialIdentity = captureIdentityScope()
  const {
    method = 'GET',
    body,
    headers = {},
    signal,
    withAuth = true,
    skipDeduplication = false,
    skipOrgContext = false,
    skipJsonParse = false,
    silentAuthFailure = false,
  } = config

  if (withAuth && !endpoint.startsWith('/auth/')) {
    // Wait for initial auth check, then for auth store to be ready. Both
    // gates only print a single trace line each so a normal request shows
    // up at most twice in the console at debug level.
    await waitForInitialAuthCheck()
    await Promise.race([
      waitForAuthReady(),
      new Promise<void>(resolve => setTimeout(resolve, AUTH_READY_TIMEOUT_MS)),
    ])
    if (!isInitialAuthCheckComplete || isRefreshInProgress) {
      logger.debug(
        `[ApiClient] ${endpoint} (boot=${!isInitialAuthCheckComplete}, refresh=${isRefreshInProgress})`,
      )
    }
  }

  if (
    withAuth &&
    isRefreshInProgress &&
    pendingRefreshPromise &&
    !endpoint.includes('/auth/refresh')
  ) {
    logger.debug(
      `[ApiClient] Request to ${endpoint} waiting for in-progress refresh`,
    )
    await pendingRefreshPromise
    logger.debug(
      `[ApiClient] Request to ${endpoint} continuing after refresh completed`,
    )
  }

  // Combine URL from API base and endpoint. Keep the pre-cache-bust URL for
  // dedupe so equivalent GETs share one in-flight request.
  const requestScope = captureRequestScope()
  const scopedRequest = withAuth && !endpoint.startsWith('/auth/')
  if (scopedRequest && initialIdentity.identity) initialIdentity.assertCurrent()
  const dedupeUrl = `${getApiUrl(endpoint)}${endpoint}`
  let url = dedupeUrl

  // Add timestamp to prevent caching for GET requests that need fresh data
  if (method === 'GET' && needsTimestamp(endpoint)) {
    // Only add timestamp if URL doesn't already have one to prevent double timestamps
    const hasTimestamp = url.includes('_t=')
    if (!hasTimestamp) {
      url += url.includes('?') ? `&_t=${Date.now()}` : `?_t=${Date.now()}`
    }
  }

  // Get current organization ID if needed for this endpoint (needed for deduplication key)
  let organizationHeaders: Record<string, string> = {}
  let currentOrgId: string | null = null
  let currentExternalRelationshipId: string | null = null

  if (!skipOrgContext && needsOrganizationId(endpoint)) {
    // First, check if there's an organization set in the store
    // This takes priority over URL-based checks to avoid race conditions during navigation
    const { orgId, slug, externalRelationshipId } =
      await getOrganizationContext()

    // Check if we're on the organizations page
    const isOnOrganizationsPage =
      typeof window !== 'undefined' &&
      window.location.pathname.startsWith('/organizations')

    debugLog(
      `[ApiClient] Organization context check for ${endpoint}: isOnOrganizationsPage=${isOnOrganizationsPage}, orgId=${orgId}, pathname=${typeof window !== 'undefined' ? window.location.pathname : 'undefined'}`,
    )

    // If we have a valid organization in the store, use it regardless of URL
    // This handles the race condition during navigation where URL hasn't updated yet
    if (orgId) {
      currentOrgId = orgId
      currentExternalRelationshipId = externalRelationshipId
      debugLog(
        `[ApiClient] Adding organization context to ${endpoint}: ID=${orgId}, slug=${slug || 'none'}`,
      )
      organizationHeaders = {
        'x-organization-id': orgId,
        ...(slug && { 'x-organization-slug': slug }),
        ...(externalRelationshipId && {
          'x-external-org-relationship-id': externalRelationshipId,
        }),
      }
    } else if (!isOnOrganizationsPage) {
      // No org in store and not on organizations page - wait for context
      const authState = useAuthStore.getState()
      if (authState.isAuthenticated) {
        await waitForOrganizationContext()
        // Re-check after waiting
        const freshContext = await getOrganizationContext()
        currentOrgId = freshContext.orgId
        currentExternalRelationshipId = freshContext.externalRelationshipId
        if (freshContext.orgId) {
          debugLog(
            `[ApiClient] Adding organization context after wait to ${endpoint}: ID=${freshContext.orgId}, slug=${freshContext.slug || 'none'}`,
          )
          organizationHeaders = {
            'x-organization-id': freshContext.orgId,
            ...(freshContext.slug && {
              'x-organization-slug': freshContext.slug,
            }),
            ...(freshContext.externalRelationshipId && {
              'x-external-org-relationship-id':
                freshContext.externalRelationshipId,
            }),
          }
        } else {
          debugWarn(
            '[ApiClient] Organization context is needed but not available',
          )
        }
      }
    } else {
      debugLog(
        `[ApiClient] On organizations page with no org set, skipping org context for ${endpoint}`,
      )
      // Force clear the organization context cache when on organizations page with no org
      clearOrganizationContextCache()

      // For endpoints that require organization context, return an empty/null response
      // instead of making a request that will fail. This allows React Query to handle
      // the transition gracefully without showing errors or triggering retries.
      debugLog(
        `[ApiClient] Skipping request to ${endpoint} - no organization context on organizations page`,
      )
      return {
        data: null,
        message: 'No organization context available',
      } as TResponse
    }
  }

  if (scopedRequest) requestScope.assertCurrent()
  // Check for duplicate in-flight request (now with organization context)
  if (
    !skipDeduplication &&
    !signal &&
    Object.keys(headers).length === 0 &&
    shouldDeduplicate(endpoint, method)
  ) {
    trackApiTraffic(endpoint, method)

    const requestKey = getRequestKey(
      method,
      dedupeUrl,
      body,
      currentOrgId || undefined,
      currentExternalRelationshipId || undefined,
    )

    // Clean expired requests before checking
    cleanExpiredRequests()

    // If we have a pending request for the same key, return its promise
    if (pendingRequests[requestKey]) {
      trackApiTraffic(endpoint, method, true)
      debugLog(
        `[ApiClient] Deduplicating request: ${method} ${endpoint} (org: ${currentOrgId || 'none'})`,
      )
      return pendingRequests[requestKey].promise as Promise<TResponse>
    }
  }

  // Get desktop auth header if running in Tauri
  const desktopAuthHeader = await getDesktopAuthHeader(endpoint, withAuth)
  if (scopedRequest) requestScope.assertCurrent()

  // Prepare fetch options with credentials always included
  const fetchOptions: RequestInit = {
    ...getDefaultFetchOptions(),
    method,
    signal,
    credentials: 'include', // Always include credentials regardless of withAuth
    headers: {
      ...getDefaultFetchOptions().headers,
      ...desktopAuthHeader, // Add desktop auth header if available
      ...organizationHeaders,
      ...headers, // User headers override defaults and org headers
    },
  }

  // Add body for non-GET requests
  if (body && method !== 'GET') {
    fetchOptions.body = body instanceof FormData ? body : JSON.stringify(body)

    // Remove Content-Type header for FormData requests
    if (body instanceof FormData) {
      const headers = new Headers(fetchOptions.headers)
      headers.delete('Content-Type')
      fetchOptions.headers = headers
    }
  }

  // Make the request
  const makeRequest = async (retryAttempt = 0): Promise<TResponse> => {
    try {
      // Request logging disabled - enable DEBUG_MODE for debugging
      await waitForGetRequestSlot(method, endpoint)

      if (scopedRequest) requestScope.assertCurrent()
      const linked = linkAbortSignals([
        signal,
        scopedRequest ? requestScope.signal : undefined,
      ])
      let response: Response
      try {
        response = await fetchWithDeadline(url, {
          ...fetchOptions,
          signal: linked.signal,
        })
      } finally {
        linked.dispose()
      }
      if (scopedRequest) requestScope.assertCurrent()
      const responseRequestId = response.headers.get('x-request-id')
      if (responseRequestId) {
        setLoggerRequestId(responseRequestId)
      }

      // Handle API errors
      if (!response.ok) {
        if (
          response.status === 429 &&
          method === 'GET' &&
          retryAttempt < GET_RETRY_MAX_ATTEMPTS
        ) {
          const retryDelayMs = getRetryAfterDelayMs(response, retryAttempt)
          logger.warn(
            `[ApiClient] Rate limited ${endpoint}; retrying in ${Math.round(retryDelayMs)}ms`,
          )
          await delay(retryDelayMs)
          return makeRequest(retryAttempt + 1)
        }

        const errorText = await response.text()
        if (scopedRequest) requestScope.assertCurrent()
        const parsedError = parseApiErrorText(
          errorText,
          response.status === 500 ? 'Internal server error' : 'Request failed',
        )

        // Don't log 401 errors immediately if we'll attempt a token refresh
        // This prevents noisy console errors that will be resolved by the refresh
        const willAttemptRefresh =
          response.status === 401 &&
          withAuth &&
          !silentAuthFailure &&
          !endpoint.includes('/auth/refresh') &&
          refreshAttemptBudget.canAttempt()

        const shouldLogAuthFailure = !(
          silentAuthFailure &&
          (response.status === 401 || response.status === 403)
        )

        if (!willAttemptRefresh && shouldLogAuthFailure) {
          debugError(`[ApiClient] Error ${response.status}: ${errorText}`)
        }

        // Log additional context for debugging specific endpoints
        // Skip logging for 401 errors that will attempt a refresh (same as generic error logging)
        if (
          endpoint.includes('/organization-invites/') &&
          !willAttemptRefresh
        ) {
          // Log organization invites errors, even in production, to help debug issues
          logger.error(
            `[ApiClient] Organization invites error - URL: ${url}, Status: ${response.status}, Org: ${organizationHeaders['x-organization-id'] || 'none'}`,
          )

          // Also log the full error response for debugging
          try {
            const errorData = errorText ? JSON.parse(errorText) : null
            logger.error(
              `[ApiClient] Organization invites error details:`,
              errorData,
            )
          } catch (parseError) {
            logger.error(
              `[ApiClient] Organization invites error text:`,
              errorText,
            )
          }
        }

        // Special handling for 403 errors.
        if (response.status === 403) {
          // Check if this is a suspended user error
          let errorData: { message?: string } | null = null
          if (parsedError.data && typeof parsedError.data === 'object') {
            errorData = parsedError.data as { message?: string }
          }

          // Handle suspended user gracefully - check for specific error messages from access guard
          const isSuspendedUserError =
            errorText
              .toLowerCase()
              .includes('user account is suspended in this organization') ||
            errorText
              .toLowerCase()
              .includes('user account is inactive in this organization') ||
            (errorData &&
              (errorData.message
                ?.toLowerCase()
                .includes('user account is suspended in this organization') ||
                errorData.message
                  ?.toLowerCase()
                  .includes('user account is inactive in this organization')))

          // Check for organization-level access denial (when user is not a member)
          const isOrganizationAccessDenied =
            errorText.toLowerCase().includes('not a member of organization') ||
            errorText.toLowerCase().includes('no access to organization') ||
            (errorData &&
              (errorData.message
                ?.toLowerCase()
                .includes('not a member of organization') ||
                errorData.message
                  ?.toLowerCase()
                  .includes('no access to organization')))

          if (
            'x-organization-id' in organizationHeaders &&
            (isSuspendedUserError || isOrganizationAccessDenied)
          ) {
            debugError(
              `[ApiClient] Access denied for organization ${organizationHeaders['x-organization-id']}`,
            )
          }

          if (isSuspendedUserError || isOrganizationAccessDenied) {
            logger.warn(
              '[ApiClient] Detected suspended user or organization access issue, redirecting...',
            )

            // Don't redirect if we're already on the organizations page
            const isAlreadyOnOrgsPage =
              typeof window !== 'undefined' &&
              (window.location.pathname === '/organizations' ||
                window.location.pathname.startsWith('/organizations?'))

            if (!isAlreadyOnOrgsPage) {
              // Show toast notification for suspended user
              try {
                toast.error(
                  'Your access to this organization has been suspended. Redirecting to organizations page...',
                )

                // Clear organization context and redirect
                clearOrganizationContextCache()

                // Use window.location for reliable redirect
                setTimeout(() => {
                  window.location.href = '/organizations?suspended=true'
                }, 1500)

                throw new ApiError(
                  'Account suspended',
                  response.status,
                  errorData,
                )
              } catch (toastError) {
                // Fallback if toast fails
                logger.error(
                  '[ApiClient] Could not show toast for suspended user handling:',
                  toastError,
                )
                window.location.href = '/organizations?suspended=true'
                throw new ApiError(
                  'Account suspended',
                  response.status,
                  errorData,
                )
              }
            } else {
              // If we're already on orgs page, clear the current organization to prevent further API calls
              try {
                const { setCurrentOrganization } =
                  useOrganizationStore.getState()
                setCurrentOrganization(null)
              } catch (error) {
                logger.error(
                  '[ApiClient] Could not clear current organization:',
                  error,
                )
              }
              throw new ApiError(
                'Account suspended',
                response.status,
                errorData,
              )
            }
          }

          // NOTE: previous behaviour also treated *any* 403 on "membership-style"
          // endpoints (/permissions/members, /user-preferences, /subscriptions, etc.)
          // as a suspended-user signal and force-redirected to /organizations.
          // That heuristic broke external-org users, who legitimately get 403
          // on those endpoints because @DenyExternalUsers / their PermissionSet
          // forbids them — they aren't suspended. AccessGuard now throws an
          // explicit "User account is suspended in this organization" message
          // for true suspensions, which is caught by the block above.

          // For all other 403 errors (like deleting project members without permission),
          // use the actual error message from the backend if available (e.g., subscription limit errors)
          let permissionErrorMessage =
            "You don't have permission to perform this action"
          if (errorData?.message) {
            permissionErrorMessage = errorData.message
          } else if (
            parsedError.message &&
            parsedError.message !== 'Request failed'
          ) {
            permissionErrorMessage = parsedError.message
          }
          throw new ApiError(permissionErrorMessage, response.status, errorData)
        }

        // Handle 500 errors with more context
        if (response.status === 500) {
          debugError(
            `[ApiClient] Server error for ${endpoint} - this may indicate a backend service issue`,
          )
          throw new ApiError(
            parsedError.message,
            response.status,
            parsedError.data ?? errorText,
          )
        }

        // If the error is an authentication error (401) and we're not already trying to refresh
        if (
          response.status === 401 &&
          withAuth &&
          !endpoint.includes('/auth/refresh')
        ) {
          logger.info(
            `[ApiClient] Got 401 for ${endpoint}, refreshAttemptCount=${refreshAttemptBudget.count}`,
          )
          const now = Date.now()

          // Check if we've exceeded max refresh attempts
          if (!refreshAttemptBudget.canAttempt(now)) {
            if (isTransientRefreshFailure(lastRefreshFailureReason)) {
              logger.warn(
                '[ApiClient] Max refresh attempts reached due to transient failures; preserving session',
              )
              throw new ApiError(
                'Authentication temporarily unavailable',
                503,
                {
                  reason: lastRefreshFailureReason,
                },
              )
            }

            logger.warn('[ApiClient] Max refresh attempts reached')
            // Clear any stored tokens on desktop
            if (isTauri()) {
              desktopSecureStorage.deleteTokens().catch(() => {})
              clearWebViewCookies().catch(() => {})
            }
            // Only redirect if not already on a public auth page (prevents infinite loop)
            if (!isOnPublicAuthPage() && !hasRedirectedToLogin) {
              hasRedirectedToLogin = true
              logger.warn('[ApiClient] Redirecting to login')
              window.location.href = '/login'
            }
            throw new Error(
              'Authentication failed - max refresh attempts reached',
            )
          }

          if (isTauri()) {
            const tokens = await desktopSecureStorage.getTokens()
            if (scopedRequest) requestScope.assertCurrent()
            if (!tokens?.refreshToken) {
              logger.warn('[ApiClient] No desktop refresh token available')
              desktopSecureStorage.deleteTokens().catch(() => {})
              clearWebViewCookies().catch(() => {})
              if (!isOnPublicAuthPage() && !hasRedirectedToLogin) {
                hasRedirectedToLogin = true
                window.location.href = '/login'
              }
              throw new Error('Authentication failed - no refresh token')
            }
          }

          logger.info(
            `[ApiClient] Attempting token refresh for 401 on ${endpoint}`,
          )

          try {
            if (scopedRequest) requestScope.assertCurrent()
            const refreshSuccess = await refreshAccessToken()
            if (scopedRequest) requestScope.assertCurrent()

            if (refreshSuccess) {
              logger.info(
                `[ApiClient] Token refresh succeeded, retrying ${endpoint}`,
              )
              // Retry the original request after successful refresh
              return apiClient<TResponse>(endpoint, {
                ...config,
                skipDeduplication: true, // Skip deduplication for the retry
              })
            } else {
              if (isTransientRefreshFailure(lastRefreshFailureReason)) {
                throw new ApiError(
                  'Authentication temporarily unavailable',
                  503,
                  {
                    reason: lastRefreshFailureReason,
                  },
                )
              }
              throw new Error('Token refresh returned false')
            }
          } catch (refreshError) {
            if (scopedRequest) requestScope.assertCurrent()
            if (
              refreshError instanceof ApiError &&
              refreshError.status === 503 &&
              (
                refreshError.data as
                  | { reason?: RefreshFailureReason }
                  | undefined
              )?.reason === 'transient'
            ) {
              logger.warn(
                '[ApiClient] Preserving session after transient refresh failure',
              )
              throw refreshError
            }

            logger.error('[ApiClient] Token refresh failed:', refreshError)
            // Clear any stored tokens on desktop
            if (isTauri()) {
              desktopSecureStorage.deleteTokens().catch(() => {})
              clearWebViewCookies().catch(() => {})
            }
            // Only redirect if not already on a public auth page (prevents infinite loop)
            if (!isOnPublicAuthPage() && !hasRedirectedToLogin) {
              hasRedirectedToLogin = true
              logger.warn(
                '[ApiClient] Token refresh failed, redirecting to login',
              )
              window.location.href = '/login'
            }
            throw new Error('Authentication failed')
          }
        }

        throw new ApiError(
          parsedError.message,
          response.status,
          parsedError.data ?? errorText,
        )
      }

      // Auth cookie debugging disabled - enable DEBUG_MODE for debugging

      // Parse response as JSON if not skipped
      if (skipJsonParse) {
        // Return a simple success response for endpoints that don't return JSON
        return { success: true } as unknown as TResponse
      } else {
        // Check if response is empty before trying to parse as JSON
        const contentLength = response.headers.get('content-length')
        const isEmpty =
          contentLength === '0' ||
          (await response
            .clone()
            .text()
            .then(text => text.trim() === ''))

        if (isEmpty) {
          // Return empty success object for empty responses
          debugLog(
            `[ApiClient] Empty response from ${endpoint}, returning success object`,
          )
          return { success: true } as unknown as TResponse
        }

        // Try to parse as JSON for non-empty responses
        try {
          const data = await response.json()
          return data
        } catch (error) {
          // Log more details for import endpoints to help debug
          if (endpoint.includes('/import')) {
            const rawText = await response.clone().text()
            logger.error(
              `[ApiClient] JSON parse error for import endpoint: ${endpoint}`,
              {
                error,
                errorMessage:
                  error instanceof Error ? error.message : String(error),
                rawTextPreview: rawText.substring(0, 500),
                responseStatus: response.status,
                responseHeaders: Object.fromEntries(response.headers.entries()),
              },
            )
          }
          debugWarn(
            `[ApiClient] Could not parse JSON response from ${endpoint}`,
            error,
          )
          // Return empty success object as fallback
          return { success: true } as unknown as TResponse
        }
      }
    } catch (error) {
      if (isRequestAbortError(error)) {
        debugLog(`[ApiClient] Request aborted: ${method} ${endpoint}`)
        throw error
      }

      const shouldLogRequestFailure = !(
        silentAuthFailure &&
        error instanceof ApiError &&
        (error.status === 401 || error.status === 403)
      )
      if (shouldLogRequestFailure) {
        debugError('[ApiClient] Request failed:', error)
      }
      throw error
    }
  }

  // For requests that should be deduplicated, store the promise
  if (
    !skipDeduplication &&
    !signal &&
    Object.keys(headers).length === 0 &&
    shouldDeduplicate(endpoint, method)
  ) {
    trackApiTraffic(endpoint, method)
    const requestKey = getRequestKey(
      method,
      dedupeUrl,
      body,
      currentOrgId || undefined,
      currentExternalRelationshipId || undefined,
    )

    // Create a promise and store it in the pending requests
    const requestPromise = makeRequest().then(result => {
      if (scopedRequest) requestScope.assertCurrent()
      return result
    })
    pendingRequests[requestKey] = {
      promise: requestPromise,
      timestamp: Date.now(),
    }

    // Clean up the pending request after it completes
    requestPromise
      .then(() => {
        setTimeout(() => {
          delete pendingRequests[requestKey]
        }, 100) // Short delay to handle near-simultaneous duplicate requests
      })
      .catch(() => {
        delete pendingRequests[requestKey]
      })

    return requestPromise
  }

  // For requests that shouldn't be deduplicated, just make the request
  const result = await makeRequest()
  if (scopedRequest) requestScope.assertCurrent()
  return result
}

// Export function to clear organization context cache (useful when switching organizations)
export function clearOrganizationContextCache(): void {
  cachedOrganizationContext = null
}

// Export function to clear pending requests cache (useful when switching organizations)
export function clearPendingRequestsCache(): void {
  Object.keys(pendingRequests).forEach(key => {
    delete pendingRequests[key]
  })
}

// Export function to reset refresh state (useful for logout/testing)
export function resetRefreshState(): void {
  refreshAttemptBudget.reset()
  isRefreshInProgress = false
  pendingRefreshPromise = null
  hasRedirectedToLogin = false
  lastRefreshFailureReason = 'none'
}

/**
 * Public API client for endpoints that don't require authentication
 * Skips authentication and organization context
 */
export async function apiClientPublic<TResponse, TRequest = unknown>(
  endpoint: string,
  config: Omit<ApiRequestConfig<TRequest>, 'withAuth' | 'skipOrgContext'> = {},
): Promise<TResponse> {
  return apiClient<TResponse, TRequest>(endpoint, {
    ...config,
    withAuth: false,
    skipOrgContext: true,
  })
}
